import type { AnonymizationResult } from "../domain/engine";
import type { WorkerInboundMessage, WorkerOutboundMessage } from "./engine.worker";

export interface EngineRunHandle {
  result: Promise<AnonymizationResult>;
  cancel: () => void;
}

/**
 * Cliente del Web Worker del motor. Cada llamada crea un Worker dedicado (se
 * destruye al terminar) para evitar estado compartido entre ejecuciones y
 * simplificar la cancelación. El procesamiento pesado nunca corre en el hilo
 * principal (05-arquitectura-tecnica.md).
 */
export function runEngine(input: unknown): EngineRunHandle {
  const runId = `client_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const worker = new Worker(new URL("./engine.worker.ts", import.meta.url), { type: "module" });

  let settle: (result: AnonymizationResult) => void = () => {};
  let fail: (error: Error) => void = () => {};

  const result = new Promise<AnonymizationResult>((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });

  worker.addEventListener("message", (event: MessageEvent<WorkerOutboundMessage>) => {
    const message = event.data;
    if (message.runId !== runId) return;
    if (message.type === "result") {
      settle(message.result);
    } else {
      fail(new Error(message.message));
    }
    worker.terminate();
  });

  worker.addEventListener("error", (event: ErrorEvent) => {
    fail(new Error(event.message || "Fallo interno no clasificado en el worker."));
    worker.terminate();
  });

  const runMessage: WorkerInboundMessage = { type: "run", runId, input };
  worker.postMessage(runMessage);

  return {
    result,
    cancel: () => {
      const cancelMessage: WorkerInboundMessage = { type: "cancel", runId };
      worker.postMessage(cancelMessage);
    },
  };
}
